import React, { useState, useEffect, useCallback } from 'react';
import { Upload, Download, Trash2, Eye, Loader2, FileText } from 'lucide-react';
import api from '../../utils/api';
import { formatRecordDate } from '../../utils/dateUtils';
import { DOCUMENT_LABEL_SUGGESTIONS } from '../../utils/personnelLabels';
import toast from 'react-hot-toast';

const DocumentsTab = ({ personnelId }) => {
    const [documents, setDocuments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [label, setLabel] = useState('Kimlik');
    const [file, setFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewDoc, setPreviewDoc] = useState(null);

    const fetchDocuments = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await api.get(`/personnel/${personnelId}/documents`);
            setDocuments(res.data || []);
        } catch (error) {
            toast.error('Belgeler yüklenemedi.');
        } finally {
            setIsLoading(false);
        }
    }, [personnelId]);

    useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

    // Onizleme URL'lerini temizle
    useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

    const handleUpload = async (e) => {
        e.preventDefault();
        if (!file) {
            toast.error('Lütfen bir dosya seçin.');
            return;
        }
        setIsUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('label', label);
            await api.post(`/personnel/${personnelId}/documents`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            toast.success('Belge yüklendi.');
            setFile(null);
            e.target.reset();
            fetchDocuments();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Belge yüklenemedi.');
        } finally {
            setIsUploading(false);
        }
    };

    const handleDownload = async (doc) => {
        try {
            const res = await api.get(`/personnel/documents/${doc.id}/download`, { responseType: 'blob' });
            const url = URL.createObjectURL(res.data);
            const link = document.createElement('a');
            link.href = url;
            link.download = doc.original_name;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
        } catch (error) {
            toast.error('İndirilemedi.');
        }
    };

    const handlePreview = async (doc) => {
        try {
            const res = await api.get(`/personnel/documents/${doc.id}/content`, { responseType: 'blob' });
            if (previewUrl) URL.revokeObjectURL(previewUrl);
            setPreviewUrl(URL.createObjectURL(res.data));
            setPreviewDoc(doc);
        } catch (error) {
            toast.error('Önizleme yüklenemedi.');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Bu belgeyi kalıcı olarak silmek istediğinize emin misiniz?')) return;
        try {
            await api.delete(`/personnel/documents/${id}`);
            toast.success('Belge silindi.');
            if (previewDoc?.id === id) { setPreviewDoc(null); setPreviewUrl(null); }
            fetchDocuments();
        } catch (error) {
            toast.error('Silinemedi.');
        }
    };

    const isPreviewable = (doc) => /image\/|pdf/.test(doc.file_type || '');
    const inputClass = 'px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg focus:outline-none focus:border-primary-500 text-white text-sm';

    return (
        <div className="space-y-5">
            {/* Yükleme formu */}
            <form onSubmit={handleUpload} className="glass border border-dark-border rounded-xl p-4 flex flex-col md:flex-row gap-3 items-stretch md:items-end">
                <div className="flex flex-col gap-1">
                    <label className="text-xs text-dark-muted">Belge Türü</label>
                    <input type="text" list="doc-labels" value={label} onChange={(e) => setLabel(e.target.value)} className={inputClass} placeholder="Örn: Kimlik" />
                    <datalist id="doc-labels">
                        {DOCUMENT_LABEL_SUGGESTIONS.map((s) => <option key={s} value={s} />)}
                    </datalist>
                </div>
                <div className="flex flex-col gap-1 flex-1">
                    <label className="text-xs text-dark-muted">Dosya (pdf, jpg, png, webp, doc, xls — maks. 20 MB)</label>
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx" onChange={(e) => setFile(e.target.files[0])} className="text-sm text-dark-muted file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-primary-600 file:text-white file:text-sm file:cursor-pointer" />
                </div>
                <button type="submit" disabled={isUploading} className="flex items-center justify-center gap-1 bg-primary-600 hover:bg-primary-500 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">
                    {isUploading ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />} Yükle
                </button>
            </form>

            {/* Liste */}
            <div className="glass border border-dark-border rounded-xl overflow-hidden">
                <div className="mobile-table-hint lg:hidden">Belge ayrıntıları ve işlemler için tabloyu yatay kaydırın →</div>
                <div className="mobile-table-scroll overflow-x-auto">
                    <table className="mobile-complete-table w-full text-left text-sm whitespace-nowrap">
                        <thead className="bg-dark-card text-dark-muted border-b border-dark-border uppercase tracking-wider text-[11px]">
                            <tr>
                                <th className="px-4 py-3 font-semibold">Tür</th>
                                <th className="px-4 py-3 font-semibold">Dosya</th>
                                <th className="px-4 py-3 font-semibold">Tarih</th>
                                <th className="px-4 py-3 font-semibold text-right">İşlem</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-dark-border">
                            {isLoading ? (
                                <tr><td colSpan="4" className="px-4 py-8 text-center text-dark-muted"><Loader2 className="animate-spin inline" size={20} /></td></tr>
                            ) : documents.length === 0 ? (
                                <tr><td colSpan="4" className="px-4 py-8 text-center text-dark-muted">Belge bulunamadı.</td></tr>
                            ) : (
                                documents.map((doc) => (
                                    <tr key={doc.id} className="hover:bg-dark-hover transition-colors">
                                        <td className="px-4 py-3">
                                            <span className="inline-flex items-center px-2 py-1 rounded bg-dark-card text-xs font-medium text-dark-text border border-dark-border">{doc.label || 'Belge'}</span>
                                        </td>
                                        <td className="px-4 py-3 text-dark-text flex items-center gap-2 max-w-[220px] truncate" title={doc.original_name}>
                                            <FileText size={16} className="text-dark-muted shrink-0" /> {doc.original_name}
                                        </td>
                                        <td className="px-4 py-3 text-dark-muted">{formatRecordDate(doc.createdAt)}</td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                {isPreviewable(doc) && (
                                                    <button onClick={() => handlePreview(doc)} className="p-1.5 text-dark-muted hover:text-primary-500 hover:bg-primary-500/10 rounded-lg transition-colors" title="Önizle">
                                                        <Eye size={16} />
                                                    </button>
                                                )}
                                                <button onClick={() => handleDownload(doc)} className="p-1.5 text-dark-muted hover:text-green-500 hover:bg-green-500/10 rounded-lg transition-colors" title="İndir">
                                                    <Download size={16} />
                                                </button>
                                                <button onClick={() => handleDelete(doc.id)} className="p-1.5 text-dark-muted hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors" title="Sil">
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Önizleme */}
            {previewUrl && previewDoc && (
                <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/90 backdrop-blur-sm p-4" onClick={() => { setPreviewDoc(null); setPreviewUrl(null); }}>
                    <div className="bg-dark-card border border-slate-700 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-between items-center p-4 border-b border-slate-700/50">
                            <span className="font-semibold text-dark-text truncate">{previewDoc.original_name}</span>
                            <button onClick={() => { setPreviewDoc(null); setPreviewUrl(null); }} className="text-slate-400 hover:text-white px-2">Kapat</button>
                        </div>
                        <div className="flex-1 overflow-auto bg-slate-950 flex items-center justify-center">
                            {/image\//.test(previewDoc.file_type) ? (
                                <img src={previewUrl} alt={previewDoc.original_name} className="max-w-full max-h-[75vh] object-contain" />
                            ) : (
                                <iframe src={previewUrl} title={previewDoc.original_name} className="w-full h-[75vh]" />
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DocumentsTab;
